import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import {
  downloadMedicineOrderInvoice,
  fetchMedicineOrderFilterOptions,
  fetchMedicineOrders,
  markMedicineOrderReviewed,
  updateMedicineOrderStatuses,
} from "api/medicineOrderImproved/medicineOrderImprovedApi";
import ScreenLoader from "views/Components/screenLoader";
import "./medicineOrderImproved.css";

const ORDER_STATUSES = {
  1: "Submitted",
  2: "Processing",
  3: "Dispatched",
  4: "Completed",
  5: "Cancelled",
  6: "Attempted",
  7: "Unreachable",
  8: "Payment Pending",
  9: "Quote Sent",
  10: "Contacted",
  11: "Packed",
  12: "Ready",
  13: "Draft",
  14: "Item Pending",
  16: "Partially Delivered",
  17: "Procurement Ready",
  18: "Hold",
  19: "Order On Move",
};

const STATUS_SEQUENCE = [
  "",
  "1",
  "2",
  "11",
  "3",
  "19",
  "8",
  "4",
  "5",
  "6",
  "7",
  "12",
  "14",
  "16",
  "17",
  "18",
  "10",
  "13",
  "9",
];

const STATUS_OPTIONS = STATUS_SEQUENCE.filter(Boolean).map((status) => ({
  value: status,
  label: ORDER_STATUSES[status],
}));

const BULK_STATUS_OPTIONS = [
  { value: "1", label: "Submitted" },
  { value: "10", label: "Contacted" },
  { value: "2", label: "Processing" },
  { value: "11", label: "Packed" },
  { value: "3", label: "Dispatched" },
  { value: "5", label: "Cancelled" },
  { value: "6", label: "Attempted" },
  { value: "7", label: "Unreachable" },
  { value: "8", label: "Pending" },
  { value: "12", label: "Ready" },
  { value: "13", label: "Draft" },
  { value: "14", label: "Item Pending" },
  { value: "16", label: "Partially Delivered" },
  { value: "17", label: "Procurement Ready" },
  { value: "18", label: "Hold" },
  { value: "19", label: "Order On Move" },
];

const SOURCE_OPTIONS = [
  { value: "1", label: "Flash" },
  { value: "2", label: "oPharma" },
  { value: "3", label: "MyPharma" },
  { value: "4", label: "Daraz" },
  { value: "5", label: "Pickme" },
  { value: "6", label: "E-channeling" },
  { value: "7", label: "Uber" },
  { value: "8", label: "Heart 2 Heart" },
  { value: "9", label: "Vida" },
  { value: "10", label: "PetDoc" },
  { value: "11", label: "LankaRemit" },
  { value: "12", label: "ComBankFlash" },
  { value: "13", label: "LiweCommunities" },
  { value: "14", label: "Synergyit" },
  { value: "15", label: "Delta" },
  { value: "17", label: "DirectPay" },
];

const PAYMENT_OPTIONS = [
  "Cash",
  "Card",
  "Cheque",
  "Bank Transfer",
  "Hybrid",
  "CeyPay",
  "QChatPay",
  "Swap Machine",
  "Mintpay",
];

const CHANNEL_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "app", label: "App" },
  { value: "web", label: "Web" },
  { value: "flash_consult", label: "First Advice" },
];

const ORDER_TYPE_OPTIONS = [
  { value: "1", label: "Medicine Order" },
  { value: "2", label: "Lab Order" },
  { value: "3", label: "Subscription Order" },
];

const ORDER_SUB_TYPE_OPTIONS = [
  { value: "1", label: "B2C" },
  { value: "2", label: "B2B" },
  { value: "3", label: "PickUp" },
  { value: "4", label: "Purchase Request" },
];

const DELIVERY_TYPE_OPTIONS = [
  { value: "1", label: "Normal" },
  { value: "2", label: "Express" },
];

const initialFilters = {
  subscriber: "",
  status: "",
  source: "",
  riderId: "",
  orderDate: "",
  paymentType: "",
  b2bCompany: "",
  channel: "",
  orderType: "",
  orderSubType: "",
  orderId: "",
  poNumber: "",
  fromDate: "",
  toDate: "",
  district: "",
  branch: "",
  deliveryType: "",
  totalCostFrom: "",
  totalCostTo: "",
  isClaimable: false,
  isPartial: false,
  isCourier: false,
};

const AUTO_APPLY_FILTERS = ["status", "isClaimable", "isPartial", "isCourier"];
const FILTER_STORAGE_KEY = "medicineOrderImprovedListState";
const ORDER_LIST_CACHE_KEY = "medicineOrderImprovedListCacheV1";
const ORDER_LIST_CACHE_TTL_MS = 2 * 60 * 1000;
const ORDER_LIST_PER_PAGE = 45;

const emptyMeta = {
  currentPage: 1,
  lastPage: 1,
  total: 0,
  perPage: ORDER_LIST_PER_PAGE,
};

const emptyFilterOptions = {
  b2bCompanies: [],
  riders: [],
  branches: [],
};

const getAuthToken = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return user.token || "";
};

const getAuthUser = () => JSON.parse(localStorage.getItem("user") || "{}");

const getSavedListState = () => {
  try {
    const isMobileViewport =
      typeof window !== "undefined" && window.innerWidth <= 760;
    const savedState = JSON.parse(
      localStorage.getItem(FILTER_STORAGE_KEY) || "{}",
    );

    return {
      filters: {
        ...initialFilters,
        ...(savedState.filters || {}),
      },
      appliedFilters: {
        ...initialFilters,
        ...(savedState.appliedFilters || savedState.filters || {}),
      },
      page: Math.max(1, Number(savedState.page || 1)),
      filtersOpen: isMobileViewport
        ? false
        : savedState.filtersOpen === undefined
        ? false
        : savedState.filtersOpen,
      advancedFiltersOpen: Boolean(savedState.advancedFiltersOpen),
    };
  } catch (error) {
    return {
      filters: initialFilters,
      appliedFilters: initialFilters,
      page: 1,
      filtersOpen: false,
      advancedFiltersOpen: false,
    };
  }
};

const getActiveFilterCount = (filterValues) =>
  Object.keys(filterValues).filter((key) => {
    const value = filterValues[key];
    return value !== "" && value !== false && value !== null;
  }).length;

const getOrderListCacheKey = (filters, page) =>
  JSON.stringify({
    filters,
    page,
    recordsPerPage: ORDER_LIST_PER_PAGE,
  });

const readOrderListCache = (filters, page) => {
  try {
    const cache = JSON.parse(localStorage.getItem(ORDER_LIST_CACHE_KEY) || "{}");
    const cacheEntry = cache[getOrderListCacheKey(filters, page)];

    if (
      !cacheEntry ||
      Date.now() - Number(cacheEntry.savedAt || 0) > ORDER_LIST_CACHE_TTL_MS
    ) {
      return null;
    }

    return cacheEntry.payload || null;
  } catch (error) {
    return null;
  }
};

const writeOrderListCache = (filters, page, payload) => {
  try {
    const cache = JSON.parse(localStorage.getItem(ORDER_LIST_CACHE_KEY) || "{}");
    const cacheKeys = Object.keys(cache);

    if (cacheKeys.length > 20) {
      cacheKeys
        .sort((firstKey, secondKey) => {
          const firstSavedAt = Number(cache[firstKey].savedAt || 0);
          const secondSavedAt = Number(cache[secondKey].savedAt || 0);
          return firstSavedAt - secondSavedAt;
        })
        .slice(0, cacheKeys.length - 20)
        .forEach((cacheKey) => delete cache[cacheKey]);
    }

    cache[getOrderListCacheKey(filters, page)] = {
      savedAt: Date.now(),
      payload,
    };

    localStorage.setItem(ORDER_LIST_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    // Cache is only a speed helper; ignore storage failures.
  }
};

const getMoneyValue = (value) => {
  const numberValue = Number(String(value || 0).replace(/,/g, ""));
  return Number.isNaN(numberValue) ? 0 : numberValue;
};

const formatMoney = (value) => {
  const numberValue = getMoneyValue(value);
  return numberValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatOrderDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const getOrderRows = (payload) => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload.orders)) {
    return payload.orders;
  }

  if (payload.data && Array.isArray(payload.data.data)) {
    return payload.data.data;
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  return [];
};

const getOrderMeta = (payload) => {
  if (!payload) {
    return emptyMeta;
  }

  const pageData =
    payload.data && Array.isArray(payload.data.data) ? payload.data : payload;

  return {
    currentPage: Number(pageData.current_page || pageData.currentPage || 1),
    lastPage: Number(pageData.last_page || pageData.lastPage || 1),
    total: Number(pageData.total || 0),
    perPage: Number(pageData.per_page || pageData.perPage || ORDER_LIST_PER_PAGE),
  };
};

const getServerStatusCounts = (payload) => {
  if (!payload) {
    return null;
  }

  return (
    payload.status_counts ||
    payload.statusCounts ||
    (payload.data &&
      (payload.data.status_counts || payload.data.statusCounts)) ||
    null
  );
};

const getLeadName = (order) => {
  if (order.lead && order.lead.name) {
    return order.lead.name;
  }

  return order.subscriber_name || order.customer_name || "-";
};

const getRiderName = (order) => {
  if (order.rider && order.rider.name) {
    return order.rider.name;
  }

  return order.rider_name || "-";
};

const getOrderAddress = (order) =>
  order.address && order.address.address_line1 ? order.address.address_line1 : "-";

const minutesSince = (value) => {
  const date = new Date(value);

  if (!value || Number.isNaN(date.getTime())) {
    return 0;
  }

  return (Date.now() - date.getTime()) / 60000;
};

const getOrderWarning = (order) => {
  const orderStatus = Number(order.order_status);

  if (
    orderStatus === 2 &&
    order.process_start_time &&
    minutesSince(order.process_start_time) >= 60 * 24 * 3
  ) {
    return "Processing for 3+ days";
  }

  if (
    orderStatus === 1 &&
    Number(order.order_sub_type) === 1 &&
    order.created_at &&
    minutesSince(order.created_at) > 20
  ) {
    return "Submitted B2C order older than 20 minutes";
  }

  return "";
};

const hasColdItem = (order) =>
  order &&
  (order.is_cold_item === true ||
    order.is_item_cold === true ||
    String(order.is_cold_item).toLowerCase() === "true" ||
    String(order.is_item_cold).toLowerCase() === "true");

const shouldHighlightUnviewed = (order) =>
  String(order.is_viewed_by_admin) === "0" ||
  (Number(order.source) === 8 && Number(order.order_status) === 1);

const getEditUrl = (order) =>
  Number(order.order_sub_type) === 3
    ? `/admin/edit-pickUp/${order.id}`
    : `/admin/order-edit-beta/${order.id}`;

const SelectField = ({ label, name, value, onChange, options }) => (
  <label className="moi-field">
    <span>{label}</span>
    <select name={name} value={value} onChange={onChange}>
      <option value="">All</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const SelectFieldWithLoading = ({
  label,
  name,
  value,
  onChange,
  options,
  loading,
}) => (
  <label className="moi-field">
    <span>{label}</span>
    <select name={name} value={value} onChange={onChange}>
      <option value="">
        {loading && !options.length ? "Loading..." : "All"}
      </option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const TextField = ({ label, name, type = "text", value, onChange }) => (
  <label className="moi-field">
    <span>{label}</span>
    <input name={name} type={type} value={value} onChange={onChange} />
  </label>
);

const MedicineOrderImproved = ({ history }) => {
  const savedListState = useMemo(() => getSavedListState(), []);
  const [filters, setFilters] = useState(savedListState.filters);
  const [appliedFilters, setAppliedFilters] = useState(
    savedListState.appliedFilters,
  );
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState(emptyMeta);
  const [serverStatusCounts, setServerStatusCounts] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [expandedInfoOrderId, setExpandedInfoOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelMessage, setCancelMessage] = useState("");
  const [page, setPage] = useState(savedListState.page);
  const [filtersOpen, setFiltersOpen] = useState(savedListState.filtersOpen);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(
    savedListState.advancedFiltersOpen,
  );
  const [filterOptions, setFilterOptions] = useState(emptyFilterOptions);
  const [filtersHydrating, setFiltersHydrating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [invoiceChoiceOrderId, setInvoiceChoiceOrderId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const loadRequestIdRef = useRef(0);

  const selectedOrderSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const loadOrders = useCallback(() => {
    const token = getAuthToken();
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;

    if (!token) {
      setError("Please log in again before loading orders.");
      return;
    }

    const cachedPayload = readOrderListCache(appliedFilters, page);

    if (cachedPayload) {
      setOrders(getOrderRows(cachedPayload));
      setMeta(getOrderMeta(cachedPayload));
      setServerStatusCounts(getServerStatusCounts(cachedPayload));
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    fetchMedicineOrders(token, {
      ...appliedFilters,
      page,
      recordsPerPage: ORDER_LIST_PER_PAGE,
    })
      .then((payload) => {
        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        const nextOrders = getOrderRows(payload);
        const nextMeta = getOrderMeta(payload);
        setOrders(nextOrders);
        setMeta(nextMeta);
        setServerStatusCounts(getServerStatusCounts(payload));
        setSelectedIds([]);
        setExpandedInfoOrderId(null);
        writeOrderListCache(appliedFilters, page, payload);

        if (page < nextMeta.lastPage) {
          fetchMedicineOrders(token, {
            ...appliedFilters,
            page: page + 1,
            recordsPerPage: ORDER_LIST_PER_PAGE,
          })
            .then((nextPagePayload) => {
              writeOrderListCache(appliedFilters, page + 1, nextPagePayload);
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        if (!cachedPayload) {
          setError("Unable to load medicine orders.");
          setOrders([]);
          setMeta(emptyMeta);
        } else {
          setError("Showing saved orders. Latest refresh failed.");
        }
      })
      .finally(() => {
        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        setLoading(false);
        setRefreshing(false);
      });
  }, [appliedFilters, page]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const user = getAuthUser();

    if (!user.token) {
      return;
    }

    setFiltersHydrating(true);
    fetchMedicineOrderFilterOptions(user.token, user.country_code)
      .then((options) => {
        setFilterOptions({
          ...emptyFilterOptions,
          ...options,
        });
      })
      .finally(() => setFiltersHydrating(false));
  }, []);

  useEffect(() => {
    localStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({
        filters,
        appliedFilters,
        page,
        filtersOpen,
        advancedFiltersOpen,
      }),
    );
  }, [filters, appliedFilters, page, filtersOpen, advancedFiltersOpen]);

  useEffect(() => {
    const searchText = (filters.subscriber || "").trim();
    const appliedSearchText = (appliedFilters.subscriber || "").trim();

    if (searchText === appliedSearchText) {
      return undefined;
    }

    if (searchText.length > 0 && searchText.length < 3) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setAppliedFilters({
        ...filters,
        subscriber: searchText,
      });
      setPage(1);
    }, 650);

    return () => clearTimeout(timer);
  }, [filters, appliedFilters.subscriber]);


  const pageStatusCounts = useMemo(
    () =>
      orders.reduce((acc, order) => {
        const key = String(order.order_status || "");
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {}),
    [orders],
  );

  const statusCounts = serverStatusCounts || pageStatusCounts;

  const statusTabs = useMemo(
    () =>
      STATUS_SEQUENCE.map((status) => ({
        value: status,
        label: status ? ORDER_STATUSES[status] : "All Orders",
        count: status ? Number(statusCounts[status] || 0) : meta.total,
      })),
    [meta.total, statusCounts],
  );

  const allCurrentRowsSelected =
    orders.length > 0 &&
    orders.every((order) => selectedOrderSet.has(order.id));

  const selectedTotalCost = useMemo(
    () =>
      orders.reduce((total, order) => {
        if (!selectedOrderSet.has(order.id)) {
          return total;
        }

        return total + getMoneyValue(order.total_cost);
      }, 0),
    [orders, selectedOrderSet],
  );

  const activeFilterCount = useMemo(
    () => getActiveFilterCount(appliedFilters),
    [appliedFilters],
  );
  const visibleFilterCount = useMemo(
    () => getActiveFilterCount(filters),
    [filters],
  );

  const updateFilter = (event) => {
    const { name, type, checked, value } = event.target;
    const nextValue = type === "checkbox" ? checked : value;
    const nextFilters = {
      ...filters,
      [name]: nextValue,
    };

    setFilters(nextFilters);

    if (AUTO_APPLY_FILTERS.includes(name)) {
      setAppliedFilters(nextFilters);
      setPage(1);
    }
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(1);
  };

  const applyStatusFilter = (status) => {
    const nextFilters = {
      ...filters,
      status,
    };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPage(1);
  };

  const toggleOrderSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : prev.concat(id),
    );
  };

  const togglePageSelection = () => {
    if (allCurrentRowsSelected) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(orders.map((order) => order.id));
  };

  const toggleOrderInfo = (orderId) => {
    setExpandedInfoOrderId((currentOrderId) =>
      currentOrderId === orderId ? null : orderId,
    );
  };

  const updateBulkStatus = () => {
    const token = getAuthToken();

    if (!bulkStatus) {
      setError("Please select an order status.");
      return;
    }

    if (!selectedIds.length) {
      setError("Please select at least one order.");
      return;
    }

    if (bulkStatus === "5" && !cancelReason.trim()) {
      setError("Please enter a cancel reason.");
      return;
    }

    setUpdating(true);
    setError("");
    setSuccess("");

    updateMedicineOrderStatuses(
      token,
      selectedIds.map((id) => ({ id, status: bulkStatus })),
      cancelReason,
      cancelMessage,
    )
      .then(() => {
        setSuccess("Order status updated successfully.");
        setBulkStatus("");
        setCancelReason("");
        setCancelMessage("");
        loadOrders();
      })
      .catch(() => {
        setError("Unable to update order status.");
      })
      .finally(() => setUpdating(false));
  };

  const downloadInvoice = (orderId, invoiceMode = "") => {
    const token = getAuthToken();

    setError("");
    setSuccess("");
    setInvoiceChoiceOrderId(null);

    downloadMedicineOrderInvoice(token, orderId, invoiceMode)
      .then((response) => {
        const pdfUrl =
          response.data &&
          response.data.data &&
          response.data.data.pdf_url
            ? response.data.data.pdf_url
            : response.data && response.data.pdf_url
            ? response.data.pdf_url
            : "";

        if (!pdfUrl) {
          setError("Invoice PDF is not available for this order.");
          return;
        }

        const link = document.createElement("a");
        link.href = pdfUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.setAttribute(
          "download",
          `order_${orderId}${invoiceMode === "compressed" ? "_compressed" : ""}.pdf`,
        );
        link.click();
      })
      .catch(() => {
        setError(
          invoiceMode === "compressed"
            ? "Unable to download compressed invoice."
            : "Unable to download invoice.",
        );
      });
  };

  const openEditOrder = (event, order) => {
    event.preventDefault();

    const goToEdit = () => history.push(getEditUrl(order));

    if (!shouldHighlightUnviewed(order)) {
      goToEdit();
      return;
    }

    const token = getAuthToken();
    setOrders((currentOrders) =>
      currentOrders.map((currentOrder) =>
        currentOrder.id === order.id
          ? { ...currentOrder, is_viewed_by_admin: "1" }
          : currentOrder,
      ),
    );

    if (!token) {
      goToEdit();
      return;
    }

    markMedicineOrderReviewed(token, order.id).finally(goToEdit);
  };

  const startCancelForOrder = (orderId) => {
    setSelectedIds([orderId]);
    setBulkStatus("5");
    setError("Enter the cancel reason, then click Change Order Status.");
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > meta.lastPage || nextPage === page) {
      return;
    }

    setPage(nextPage);
  };

  const paginationPages = useMemo(() => {
    const pages = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(meta.lastPage, page + 2);

    for (let nextPage = start; nextPage <= end; nextPage += 1) {
      pages.push(nextPage);
    }

    return pages;
  }, [meta.lastPage, page]);

  return (
    <div className="main-content medicine-order-improved">
      <div className="moi-shell">
        <div className="moi-page-head">
          <div>
            <p className="moi-kicker">Medicine Order Improved</p>
            <div className="moi-title-line">
              <h3>Medicine Orders</h3>
              <span className="moi-beta-badge">Beta</span>
            </div>
          </div>
          <div className="moi-head-actions">
            <button
              type="button"
              className="moi-icon-button"
              title="Refresh orders"
              onClick={loadOrders}
              disabled={loading || refreshing}
            >
              <i className="fa fa-refresh" />
            </button>
            <button
              type="button"
              className="moi-primary-button"
              onClick={() => history.push("/admin/add-order-beta")}
            >
              Add Order New <i className="fa fa-plus" />
            </button>
          </div>
        </div>

        {(error || success) && (
          <div className={`moi-alert ${error ? "is-error" : "is-success"}`}>
            {error || success}
          </div>
        )}

        <section className="moi-bulk-panel">
          <div className="moi-bulk-controls">
            <select
              value={bulkStatus}
              onChange={(event) => setBulkStatus(event.target.value)}
            >
              <option value="">Select Status</option>
              {BULK_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="moi-secondary-button"
              onClick={updateBulkStatus}
              disabled={!selectedIds.length || !bulkStatus || updating}
            >
              {updating ? "Changing Status" : "Change Order Status"}
            </button>
            <span className="moi-selected-count">
              {selectedIds.length} selected
              <strong>Total: {formatMoney(selectedTotalCost)}</strong>
            </span>
          </div>
          {bulkStatus === "5" && (
            <div className="moi-cancel-fields">
              <input
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
                placeholder="Cancel reason"
              />
              <input
                value={cancelMessage}
                onChange={(event) => setCancelMessage(event.target.value)}
                placeholder="Cancel message"
              />
            </div>
          )}
        </section>

        <section className="moi-status-tabs" aria-label="Order status filters">
          {statusTabs.map((status) => (
            <button
              type="button"
              key={status.value || "all"}
              className={
                String(appliedFilters.status || "") === String(status.value)
                  ? "is-active"
                  : ""
              }
              onClick={() => applyStatusFilter(status.value)}
            >
              <span>{status.label}</span>
              <b>{status.count}</b>
            </button>
          ))}
        </section>

        <section className="moi-filter-panel">
          <div className="moi-filter-head">
            <button
              type="button"
              className="moi-filter-toggle"
              onClick={() => setFiltersOpen((current) => !current)}
              aria-expanded={filtersOpen}
            >
              <i className={`fa fa-chevron-${filtersOpen ? "up" : "down"}`} />
              <span>Filters</span>
              {activeFilterCount ? (
                <strong>{activeFilterCount} active</strong>
              ) : (
                <strong>{filtersOpen ? "Open" : "Click to open"}</strong>
              )}
            </button>
            <div className="moi-filter-hint">
              Subscriber, status, and checkbox filters apply automatically.
              Other fields apply with Filter.
              {filtersHydrating ? " Loading dropdowns..." : ""}
            </div>
          </div>

          {filtersOpen && (
            <>
              <div className="moi-filter-grid">
                <TextField
                  label="Subscriber"
                  name="subscriber"
                  value={filters.subscriber}
                  onChange={updateFilter}
                />
                <TextField
                  label="Order ID"
                  name="orderId"
                  type="number"
                  value={filters.orderId}
                  onChange={updateFilter}
                />
                <SelectField
                  label="Order Status"
                  name="status"
                  value={filters.status}
                  onChange={updateFilter}
                  options={STATUS_OPTIONS}
                />
                <SelectField
                  label="Order Sub Type"
                  name="orderSubType"
                  value={filters.orderSubType}
                  onChange={updateFilter}
                  options={ORDER_SUB_TYPE_OPTIONS}
                />
                <SelectFieldWithLoading
                  label="B2B Company"
                  name="b2bCompany"
                  value={filters.b2bCompany}
                  onChange={updateFilter}
                  options={filterOptions.b2bCompanies}
                  loading={filtersHydrating}
                />
              </div>

              {advancedFiltersOpen && (
                <div className="moi-filter-grid moi-filter-grid-advanced">
                  <SelectFieldWithLoading
                    label="Rider"
                    name="riderId"
                    value={filters.riderId}
                    onChange={updateFilter}
                    options={filterOptions.riders}
                    loading={filtersHydrating}
                  />
                  <SelectField
                    label="Delivery Type"
                    name="deliveryType"
                    value={filters.deliveryType}
                    onChange={updateFilter}
                    options={DELIVERY_TYPE_OPTIONS}
                  />
                  <SelectField
                    label="Source"
                    name="source"
                    value={filters.source}
                    onChange={updateFilter}
                    options={SOURCE_OPTIONS}
                  />
                  <SelectField
                    label="Payment Type"
                    name="paymentType"
                    value={filters.paymentType}
                    onChange={updateFilter}
                    options={PAYMENT_OPTIONS.map((payment) => ({
                      value: payment,
                      label: payment,
                    }))}
                  />
                  <SelectFieldWithLoading
                    label="Branch"
                    name="branch"
                    value={filters.branch}
                    onChange={updateFilter}
                    options={filterOptions.branches}
                    loading={filtersHydrating}
                  />
                  <TextField
                    label="Order Date"
                    name="orderDate"
                    type="date"
                    value={filters.orderDate}
                    onChange={updateFilter}
                  />
                <SelectField
                  label="Channel"
                  name="channel"
                  value={filters.channel}
                  onChange={updateFilter}
                  options={CHANNEL_OPTIONS}
                />
                <SelectField
                  label="Order Type"
                  name="orderType"
                  value={filters.orderType}
                  onChange={updateFilter}
                  options={ORDER_TYPE_OPTIONS}
                />
                <TextField
                  label="PO Number"
                  name="poNumber"
                  value={filters.poNumber}
                  onChange={updateFilter}
                />
                <TextField
                  label="From Date"
                  name="fromDate"
                  type="date"
                  value={filters.fromDate}
                  onChange={updateFilter}
                />
                <TextField
                  label="To Date"
                  name="toDate"
                  type="date"
                  value={filters.toDate}
                  onChange={updateFilter}
                />
                <TextField
                  label="District ID"
                  name="district"
                  value={filters.district}
                  onChange={updateFilter}
                />
                <TextField
                  label="Min Cost"
                  name="totalCostFrom"
                  type="number"
                  value={filters.totalCostFrom}
                  onChange={updateFilter}
                />
                <TextField
                  label="Max Cost"
                  name="totalCostTo"
                  type="number"
                  value={filters.totalCostTo}
                  onChange={updateFilter}
                />
                </div>
              )}

              <div className="moi-filter-footer">
                <label className="moi-check">
                  <input
                    type="checkbox"
                    name="isClaimable"
                    checked={filters.isClaimable}
                    onChange={updateFilter}
                  />
                  <span>Is Claimable?</span>
                </label>
                <label className="moi-check">
                  <input
                    type="checkbox"
                    name="isPartial"
                    checked={filters.isPartial}
                    onChange={updateFilter}
                  />
                  <span>Is Partial?</span>
                </label>
                <label className="moi-check">
                  <input
                    type="checkbox"
                    name="isCourier"
                    checked={filters.isCourier}
                    onChange={updateFilter}
                  />
                  <span>Is Courier?</span>
                </label>
                <div className="moi-filter-actions">
                  <button
                    type="button"
                    className="moi-ghost-button"
                    onClick={() =>
                      setAdvancedFiltersOpen((current) => !current)
                    }
                  >
                    {advancedFiltersOpen ? "Less filters" : "More filters"}
                  </button>
                  <button
                    type="button"
                    className="moi-secondary-button"
                    onClick={applyFilters}
                  >
                    Filter
                  </button>
                  <button
                    type="button"
                    className={`moi-ghost-button moi-reset-button ${
                      visibleFilterCount ? "has-active-filters" : ""
                    }`}
                    onClick={resetFilters}
                    title={
                      visibleFilterCount
                        ? `Clear ${visibleFilterCount} active filter${
                            visibleFilterCount > 1 ? "s" : ""
                          }`
                        : "Reset filters"
                    }
                  >
                    Reset
                    {visibleFilterCount ? <span>{visibleFilterCount}</span> : null}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        <section className="moi-table-wrap">
          <div className="moi-table-meta">
            <span>
              Showing page {meta.currentPage} of {meta.lastPage}
              {refreshing && (
                <em className="moi-refreshing">
                  <i className="fa fa-refresh" /> Refreshing
                </em>
              )}
              {loading && (
                <em className="moi-loading-chip">
                  <i className="fa fa-circle-o-notch fa-spin" /> Loading orders
                </em>
              )}
            </span>
            <strong>{meta.total} orders</strong>
          </div>

          <div className={`moi-table-scroll ${loading ? "is-loading" : ""}`}>
            <table className="moi-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allCurrentRowsSelected}
                      onChange={togglePageSelection}
                    />
                  </th>
                  <th>#ID</th>
                  <th>Subscriber</th>
                  <th>Order Date</th>
                  <th>Total Cost</th>
                  <th>Status</th>
                  <th>Rider Name</th>
                  <th>Pending Amount</th>
                  <th>Payment Type</th>
                  <th>Invoice</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && orders.length === 0 && (
                  <tr>
                    <td colSpan="11" className="moi-empty">
                      Loading orders...
                    </td>
                  </tr>
                )}
                {!loading && orders.length === 0 && (
                  <tr>
                    <td colSpan="11" className="moi-empty">
                      No record found
                    </td>
                  </tr>
                )}
                {orders.map((order) => {
                  const leadName = getLeadName(order);
                  const mobileNumber =
                    order.lead && order.lead.mobile_no
                      ? order.lead.mobile_no
                      : "-";
                  const orderAddress = getOrderAddress(order);
                  const isInfoExpanded = expandedInfoOrderId === order.id;

                  return (
                    <React.Fragment key={order.id}>
                      <tr
                        className={`${getOrderWarning(order) ? "has-warning" : ""} ${
                          shouldHighlightUnviewed(order) ? "has-unviewed" : ""
                        } ${isInfoExpanded ? "has-info-expanded" : ""}`}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedOrderSet.has(order.id)}
                            onChange={() => toggleOrderSelection(order.id)}
                          />
                        </td>
                        <td data-label="#ID">{order.id}</td>
                        <td data-label="Subscriber">
                          <div className="moi-subscriber">
                            <span title={leadName} aria-label={leadName}>
                              {leadName}
                            </span>
                            <div>
                              {Number(order.delivery_type) === 2 && (
                                <em className="is-express">Express</em>
                              )}
                              {Number(order.order_sub_type) === 3 && (
                                <em className="is-pickup">Pickup</em>
                              )}
                              {Number(order.order_sub_type) === 4 && (
                                <em className="is-pr">PR {order.pr_id || ""}</em>
                              )}
                            </div>
                          </div>
                        </td>
                        <td data-label="Order Date">
                          {formatOrderDate(
                            order.order_display_date || order.created_at,
                          )}
                        </td>
                        <td data-label="Total Cost">
                          {formatMoney(order.total_cost)}
                        </td>
                        <td data-label="Status">
                          <div className="moi-status-cell">
                            <span
                              className={`moi-status-badge status-${order.order_status}`}
                            >
                              {ORDER_STATUSES[order.order_status] || "Unknown"}
                            </span>
                            {hasColdItem(order) && (
                              <span
                                className="moi-chip is-cold"
                                title="Cold item order"
                                aria-label="Cold item order"
                              >
                                <i className="fa fa-snowflake-o" aria-hidden="true" />
                              </span>
                            )}
                            {getOrderWarning(order) && (
                              <span
                                className="moi-warning-icon"
                                title={getOrderWarning(order)}
                                aria-label={getOrderWarning(order)}
                              >
                                <i className="fa fa-exclamation-triangle" />
                              </span>
                            )}
                            {String(order.is_claimable) === "1" && (
                              <span className="moi-chip is-claim">Claim</span>
                            )}
                            {order.partial_sent === true && (
                              <span className="moi-chip is-partial">Partial</span>
                            )}
                          </div>
                        </td>
                        <td data-label="Rider Name">{getRiderName(order)}</td>
                        <td data-label="Pending Amount">
                          {formatMoney(order.pending_amount)}
                        </td>
                        <td data-label="Payment Type">
                          {order.paymentoption || "-"}
                        </td>
                        <td data-label="Invoice">
                          <button
                            className="moi-view-link"
                            type="button"
                            onClick={() => setInvoiceChoiceOrderId(order.id)}
                          >
                            View
                          </button>
                        </td>
                        <td data-label="Actions">
                          <div className="moi-actions">
                            <Link
                              title="Edit order"
                              to={getEditUrl(order)}
                              onClick={(event) => openEditOrder(event, order)}
                            >
                              <i className="fa fa-pencil" />
                            </Link>
                            <button
                              type="button"
                              title="Order info"
                              aria-expanded={isInfoExpanded}
                              onClick={() => toggleOrderInfo(order.id)}
                            >
                              <i className="fa fa-info-circle" />
                            </button>
                            <details className="moi-action-menu">
                              <summary title="More actions">
                                <i className="fa fa-ellipsis-h" />
                              </summary>
                              <div className="moi-menu-panel">
                                <Link to={`/admin/replicate-order/${order.id}`}>
                                  <i className="fa fa-copy" /> Replicate
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => startCancelForOrder(order.id)}
                                >
                                  <i className="fa fa-window-close" /> Cancel Bill
                                </button>
                                <Link to="/admin/order_list">
                                  <i className="fa fa-wrench" /> Classic tools
                                </Link>
                              </div>
                            </details>
                          </div>
                        </td>
                      </tr>
                      {isInfoExpanded && (
                        <tr className="moi-info-row">
                          <td colSpan="11">
                            <div className="moi-info-panel">
                              <span>
                                <strong>Customer</strong>
                                {leadName}
                              </span>
                              <span>
                                <strong>Mobile</strong>
                                {mobileNumber}
                              </span>
                              <span>
                                <strong>Address</strong>
                                {orderAddress}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="moi-pagination">
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
            >
              <i className="fa fa-chevron-left" />
            </button>
            {paginationPages.map((pageNumber) => (
              <button
                type="button"
                key={pageNumber}
                className={pageNumber === page ? "is-active" : ""}
                onClick={() => goToPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={page >= meta.lastPage}
            >
              <i className="fa fa-chevron-right" />
            </button>
          </div>
        </section>
      </div>
      {invoiceChoiceOrderId && (
        <div className="moi-modal-backdrop">
          <div className="moi-invoice-modal">
            <h4>Choose Invoice Type</h4>
            <p>Use the full invoice for the standard layout, or compressed to reduce printed pages.</p>
            <div className="moi-invoice-choice-actions">
              <button
                type="button"
                className="moi-primary-button"
                onClick={() => downloadInvoice(invoiceChoiceOrderId)}
              >
                Full Invoice
              </button>
              <button
                type="button"
                className="moi-ghost-button"
                onClick={() => downloadInvoice(invoiceChoiceOrderId, "compressed")}
              >
                Compressed Invoice
              </button>
            </div>
            <button
              type="button"
              className="moi-modal-close"
              onClick={() => setInvoiceChoiceOrderId(null)}
              aria-label="Close invoice type selection"
            >
              <i className="fa fa-times" />
            </button>
          </div>
        </div>
      )}
      {updating && <ScreenLoader />}
    </div>
  );
};

export default withRouter(MedicineOrderImproved);
